<template>
  <div class="task-schedule-form-popup cols">
    <Teleport to="body">
      <div
          v-if="duplicateSchedule"
          class="task-schedule-duplicate-popup-backdrop"
          @click.self="clearDuplicateSchedulePopup"
      >
        <div class="task-schedule-duplicate-popup cols align-items-center">
          <p>
            {{ $t('settings.on-board.users.task-schedules.duplicate-driver-prefix') }}
            <b>{{ duplicateSchedule.driverName }}</b>
            <template v-if="duplicateSchedule.vehicleName">
              {{ $t('settings.on-board.users.task-schedules.duplicate-vehicle-prefix') }}
              <b>{{ duplicateSchedule.vehicleName }}</b>
            </template>
            {{ $t('settings.on-board.users.task-schedules.duplicate-suffix') }}
          </p>
          <button type="button" @click="clearDuplicateSchedulePopup">
            {{ $t('generic.close') }}
          </button>
        </div>
      </div>
    </Teleport>
    <div class="task-schedule-form-scrollable cols">
      <div class="rows mobile-cols">
        <div class="cols align-items-start mr-10">
          <label>{{ $t('task.driver') }}</label>
          <ChooseDriver v-model="driver" />
        </div>
        <div class="cols align-items-start mr-10">
          <label>{{ $t('task.vehicle') }}</label>
          <ChooseVehicles
              v-model="vehicle"
              :preselect-all="false"
              :single-select="true"
              :add-all-option="false"
              :allow-empty="true"
          />
        </div>
        <div class="cols align-items-start width-100p">
          <label for="taskSchedulePeriodLengthDays">
            {{ $t('settings.on-board.users.task-schedules.period-length-days') }}
          </label>
          <input
              id="taskSchedulePeriodLengthDays"
              type="number"
              min="1"
              max="5"
              required
              class="stretch"
              v-model.number="periodLengthDays"
          />
        </div>
      </div>
      <div class="rows mobile-cols mt-5">
        <div class="cols available-geofences-container">
          <label><b>{{ $t('settings.on-board.users.task-schedules.available-geofences') }}</b></label>
          <DataGrid
              class="ml-0 mr-0 mt-5 mb-0 strech fit-content fixed-header available-geofences"
              :data-model="availableGeoFencesDataGridModel"
              :data="availableGeoFences"
              :show-actions-table-header="false"
              @on-add-on-list="addGeoFence"
              allow-add-in-list
          >
            <template #availableGeoFenceNameHeader="{ headerText }">
              <div
                  class="pl-10 rows align-items-center width-100p"
                  @click.stop
                  @mousedown.stop
                  @keydown.stop
              >
                <span>{{ headerText }}</span>
                <input
                    v-model="availableGeoFenceSearch"
                    type="search"
                    class="ml-5 width-100p"
                    :placeholder="$t('reports.trips.search')"
                />
              </div>
            </template>
          </DataGrid>
        </div>
        <div class="cols">
          <label><b>{{ $t('settings.on-board.users.task-schedules.selected-geofences') }}</b></label>
          <DraggableDataGrid
              class="ml-0 mr-0 mt-5 mb-0 selected-geofences strech fit-content fixed-header"
              :data-model="selectedGeoFencesDataGridModel"
              :model-value="selectedGeoFencesData"
              :show-actions-table-header="false"
              @on-remove-from-list="removeGeoFence"
              @drag-change="onDragChange"
              allow-remove-from-list
          >
            <template #daysOfWeek="{ item, index }">
              <div class="task-schedule-days" @click.stop @mousedown.stop @keydown.stop>
                <button
                    v-for="day in weekdayOptions"
                    :key="day"
                    type="button"
                    class="task-schedule-day-button"
                    :class="{'selected': isDaySelected(item.daysOfWeek, day)}"
                    @click="toggleSelectedGeoFenceDay(index, day)"
                >
                  {{ $t(`settings.on-board.users.task-schedules.weekday-button.${day}`) }}
                </button>
              </div>
            </template>
          </DraggableDataGrid>
        </div>
      </div>
    </div>
    <div class="rows justify-end mt-10">
      <button type="button" class="mr-10" @click="$emit('cancel')">
        {{ $t('generic.cancel') }}
      </button>
      <button type="button" class="button-save mr-0" :disabled="!canSave" @click="save">
        {{ $t('generic.save') }}
      </button>
    </div>
  </div>
</template>

<script>
import {mapState} from "vuex";
import DataGrid from "@/components/pages/partials/DataGrid.vue";
import DraggableDataGrid from "@/components/pages/partials/DraggableDataGrid.vue";
import ChooseDriver from "@/components/pages/partials/ChooseDriver.vue";
import ChooseVehicles from "@/components/pages/partials/ChooseVehicles.vue";

export default {
  name: 'TaskScheduleFormPopup',
  components: {ChooseVehicles, ChooseDriver, DataGrid, DraggableDataGrid},
  emits: ['save', 'cancel'],
  props: {
    initialData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      weekdayOptions: [1, 2, 3, 4, 5, 6, 7],
      availableGeoFenceSearch: '',
      duplicateSchedule: null,
      driver: this.initialData?.driver ?? null,
      vehicle: this.initialData?.vehicle ?? null,
      periodLengthDays: this.initialData?.periodLengthDays ?? 1,
      selectedGeoFences: this.initialData?.geofences ?? [],
    };
  },
  computed: {
    ...mapState(['userGeoFences', 'taskSchedules', 'drivers', 'vehicles']),
    availableGeoFences() {
      const search = this.availableGeoFenceSearch.trim().toLowerCase();

      return [...(this.userGeoFences ?? [])]
          .filter(geoFence => {
            if (search.length === 0) {
              return true;
            }

            return this.getGeoFenceLabel(geoFence).toLowerCase().includes(search);
          })
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    },
    availableGeoFencesDataGridModel() {
      return {
        headers: {
          [this.$t('geo-fences.name')]: {
            prop: 'name',
            slotName: 'availableGeoFenceNameHeader',
            class: 'rows'
          },
        },
        values: {
          name: item => this.getGeoFenceLabel(item),
        },
      };
    },
    selectedGeoFencesDataGridModel() {
      return {
        headers: {
          [this.$t('task.number')]: 'numbering',
          [this.$t('geo-fences.name')]: 'name',
          [this.$t('settings.on-board.users.task-schedules.days-of-week')]: 'daysOfWeek',
        },
        values: {
          numbering: (_, index) => index + 1,
          name: item => this.getGeoFenceLabel(item),
          daysOfWeek: () => ({
            slotName: 'daysOfWeek',
            class: 'task-schedule-days-cell',
          }),
        },
      };
    },
    selectedGeoFencesData() {
      return this.selectedGeoFences.map((geoFence, index) => ({
        ...geoFence,
        id: index,
        numbering: index + 1,
      }));
    },
    canSave() {
      return !!this.driver?.id
          && Number.isInteger(this.periodLengthDays)
          && this.periodLengthDays > 0
          && this.periodLengthDays < 6
          && this.selectedGeoFences.length > 0
          && this.selectedGeoFences.every(geoFence => geoFence.daysOfWeek.length > 0);
    },
  },
  methods: {
    getGeoFenceLabel(geoFence) {
      const name = geoFence?.name ?? this.$t('settings.on-board.users.task-schedules.unknown-geofence');
      const description = geoFence?.description ?? '';

      return description ? `${name} - ${description}` : name;
    },
    isDaySelected(daysOfWeek, day) {
      return daysOfWeek.includes(day);
    },
    toggleSelectedGeoFenceDay(index, day) {
      if (this.selectedGeoFences[index] === undefined) {
        return;
      }

      const selectedDays = this.selectedGeoFences[index].daysOfWeek;
      this.selectedGeoFences[index].daysOfWeek = this.selectedGeoFences[index].daysOfWeek.includes(day)
          ? selectedDays.filter(selectedDay => selectedDay !== day)
          : [...selectedDays, day];
    },
    addGeoFence(id) {
      const lastGeoFence = this.selectedGeoFences[this.selectedGeoFences.length - 1];
      if ((lastGeoFence?.geofenceId ?? lastGeoFence?.id) === id) {
        return;
      }

      const geoFence = this.availableGeoFences.find(x => x.id === id);
      if (!geoFence) {
        return;
      }

      this.selectedGeoFences.push({
        ...geoFence,
        id: null,
        geofenceId: geoFence.id,
        daysOfWeek: [],
      });
    },
    removeGeoFence(item) {
      if (item.numbering === undefined) {
        return;
      }

      this.selectedGeoFences.splice(item.numbering - 1, 1);
    },
    onDragChange(changedData) {
      if (changedData.oldIndex === changedData.newIndex
          || changedData.oldIndex === undefined
          || changedData.newIndex === undefined) {
        return;
      }

      const draggedItem = this.selectedGeoFences[changedData.oldIndex];
      const changedItem = this.selectedGeoFences[changedData.newIndex];
      this.selectedGeoFences[changedData.newIndex] = draggedItem;
      this.selectedGeoFences[changedData.oldIndex] = changedItem;
    },
    save() {
      if (!this.canSave) {
        return;
      }

      const formData = this.getFormData();
      if (this.hasDuplicateTaskSchedule(formData)) {
        this.showDuplicateTaskSchedulePopup(formData);
        return;
      }

      this.$emit('save', formData);
    },
    getFormData() {
      return {
        vehicleId: this.vehicle?.id ?? null,
        driverId: this.driver.id,
        periodLengthDays: this.periodLengthDays,
        taskScheduleGeofences: this.selectedGeoFences.map((geoFence, index) => ({
          id: geoFence?.id ?? null,
          geofenceId: geoFence.geofenceId ?? geoFence.id,
          numbering: index + 1,
          daysOfWeek: geoFence.daysOfWeek,
        })),
      };
    },
    showDuplicateTaskSchedulePopup(formData) {
      const vehicleId = formData.vehicleId ?? null;
      this.duplicateSchedule = {
        driverName: this.getDriverName(formData.driverId),
        vehicleName: vehicleId === null ? null : this.getVehicleName(vehicleId),
      };
    },
    clearDuplicateSchedulePopup() {
      this.duplicateSchedule = null;
    },
    hasDuplicateTaskSchedule(formData) {
      const vehicleId = formData.vehicleId ?? null;
      const driverId = formData.driverId?.toString() ?? null;
      const currentScheduleId = this.initialData?.id?.toString() ?? null;

      return (this.taskSchedules ?? []).some(schedule => {
        return (schedule.id?.toString() ?? null) !== currentScheduleId
            && (schedule.driverId?.toString() ?? null) === driverId
            && ((schedule.vehicleId ?? null)?.toString() ?? null) === (vehicleId?.toString() ?? null);
      });
    },
    getDriverName(driverId) {
      const selectedDriver = this.driver?.id?.toString() === driverId?.toString() ? this.driver : null;
      return selectedDriver?.name
          ?? this.drivers.find(driver => driver.id?.toString() === driverId?.toString())?.name
          ?? this.$t('settings.on-board.users.task-schedules.unknown-driver');
    },
    getVehicleName(vehicleId) {
      const selectedVehicle = this.vehicle?.id?.toString() === vehicleId?.toString() ? this.vehicle : null;
      return selectedVehicle?.fullName
          ?? selectedVehicle?.nickname
          ?? this.vehicles.find(vehicle => vehicle.id?.toString() === vehicleId?.toString())?.fullName
          ?? this.vehicles.find(vehicle => vehicle.id?.toString() === vehicleId?.toString())?.nickname
          ?? this.$t('settings.on-board.users.task-schedules.unknown-vehicle');
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables.scss';

.task-schedule-form-scrollable {
  max-height: 72vh;
  overflow: scroll;
}

.task-schedule-duplicate-popup-backdrop {
  position: fixed;
  inset: 0;
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
}

.task-schedule-duplicate-popup {
  gap: 15px;
  max-width: 420px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
  text-align: center;
}

.task-schedule-geofence-search {
  max-width: 160px;
  min-width: 120px;
}

.available-geofences-container {
  padding-right: 10px;
}

.available-geofences {
  height: 60vh;

  &.fit-content {
    :deep(table) {
      tr {
        th {
          padding-right: 0 !important;
        }

        td {
          padding-left: 5px !important;
        }
        th, td {
          &:last-of-type {
            padding-right: 5px !important;
          }
        }
      }
    }
  }

  :deep(table) {
    tr {
      th {
        padding-right: 0 !important;
        &:last-of-type {
          width: 12% !important;
        }
      }
      td {
        padding-right: 0 !important;
        &:last-of-type {
          width: 15% !important;
        }
      }
    }
  }
}

.selected-geofences {
  height: 60vh;

  &.fit-content {
    :deep(table) {
      tr {
        th {
          padding-right: 0 !important;
        }

        td {
          padding-left: 5px !important;
        }
        th, td {
          &:last-of-type {
            padding-right: 5px !important;
          }
        }
      }
    }
  }

  :deep(table) {
    tr {
      th, td {
        &:last-of-type {
          width: 12% !important;
        }

        &:first-of-type {
          width: 60px !important;
          padding-left: 10px !important;
          padding-right: 0 !important;
        }

        &:nth-of-type(3) {
          width: 35% !important;
        }
      }
    }
  }
}

.task-schedule-days {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
}

.task-schedule-day-button {
  min-width: 26px;
  height: 26px;
  padding: 0 6px;
  background: transparent;
  border: 2px solid #b8b8b8;
  border-radius: 999px;
  color: $default-text-color;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;

  &.selected {
    background: linear-gradient(135.57deg, #FA57EE 3.72%, #B650D2 42.15%, #794BB9 75.92%, #33449C 115.52%);
    border-color: #B650D2;
    color: white;
  }
}

// Mobile:
@media (max-width: $breaking-point) {
  .available-geofences, .selected-geofences {
    height: 30vh;
  }

  .available-geofences-container {
    padding-right: 0;
  }
}

</style>
